import type { ReactNode } from 'react';
import { printVisibility } from '@/features/styles/lib/printStyles';
import { Flex } from '@/ui';
import { useOnboardingAnchor } from '../PlannerOnboarding/anchorContext';

// Shared by the bar and anything docked under it, so the two rows keep the same
// horizontal rhythm.
const BAR_PX = { base: 2, md: 4 };

type Props = {
  left?: ReactNode;
  center?: ReactNode;
  right?: ReactNode;
  // A full-width row docked beneath the bar — the Key's legend chips. It sits
  // inside the same sticky container rather than in the scrolling content, so
  // it stays on screen with the bar it belongs to.
  below?: ReactNode;
};

export const PlatformBar = ({ left, center, right, below }: Props) => {
  // The bottom of this bar is where the planner guide starts dimming for cards
  // about the chrome above it, so the whole bar has to be measurable.
  const anchorRef = useOnboardingAnchor('platformBar');

  return (
    <Flex
      bg="white"
      borderBottom="1px solid"
      borderColor="line.panel"
      direction="column"
      position="sticky"
      ref={anchorRef}
      sx={printVisibility.hide()}
      // Pin below whatever chrome is already fixed to the viewport top (the
      // CustomizeBar in customize mode — see EditPageExperience), else to 0.
      top="var(--ah-sticky-top, 0px)"
      w="100%"
      zIndex={10}
    >
      <Flex
        align="center"
        borderBottom={below ? '1px solid' : undefined}
        borderColor="line.panel"
        flexShrink={0}
        // `ml="auto"` gives no separation once the row is full.
        gap={2}
        h="ah-bar"
        px={BAR_PX}
        w="100%"
      >
        {left && (
          <Flex align="center" gap={2} minW={0}>
            {left}
          </Flex>
        )}
        {center && (
          <Flex
            align="center"
            flex="1"
            gap={2}
            justifyContent="center"
            minW={0}
          >
            {center}
          </Flex>
        )}
        {/* Icon-sized already, so it holds and the left cluster absorbs the
            crowding — the view name is the only thing here that can elide. */}
        {right && (
          <Flex align="center" flexShrink={0} gap={2} ml="auto">
            {right}
          </Flex>
        )}
      </Flex>
      {below && (
        <Flex align="center" px={BAR_PX} w="100%">
          {below}
        </Flex>
      )}
    </Flex>
  );
};
